import { Ref } from 'vue';
import { format } from 'date-fns';

import MarkerClusterer from '@googlemaps/markerclustererplus';

import mapStyles from './map-styles';
import markerClusterStyles from './marker-cluster-styles';

import {
    ApiDeviceListItem,
    ApiDeviceRoutes,
    ApiDeviceLayers,
    ApiDeviceActionsItem,
    ApiDeviceRoutesInfomodalContent,
    ApiDevicePoiInfomodalContent,
    ApiCoordinates,
    EventMarker,
} from '@/api/types/device';

import {
    ApiDeviceInfomodalContent,
    ApiDeviceRoutesListItem,
    ApiDeviceRoutesListItemPos,
    ApiDeviceRoutesListItemStop,
} from './../../../../api/types/device';

import { ILayersActive, IFencesActive } from '../../types';
import { ILayerForFilter } from '@/store/layers';
import { FilteredSearchLocation } from '@/store/device';
import deviceBgColorSelector from '@/helpers/deviceBgColorSelector';
import { colorsPalette } from '@/config/colorsPalette';

const ZOOM_LEVEL_WHEN_TO_HIDE_ROUTE_DATA_POINTS = 13;
const ZOOM_LEVEL_WHEN_TO_HIDE_ROUTE_STOP_POINTS = 11;

declare global {
    interface Window {
        mapApiInitialized: () => void;
    }
}

const MAP_ICONS = {
    markerDefault:
        'M21.7347,4.0153S17.449.0357,15,.0357H9.49C4.8981.0357,0,4.0157,0,5.24v5.51l1.9922.5692a3.8254,3.8254,0,0,1,7.6506.0432H20.3571a3.8254,3.8254,0,0,1,7.6506-.0432L30,10.75V7.3826c0-1.2245-3.0613-2.7551-8.2654-3.3673ZM11.02,4.3,3.5357,4.0417A11.333,11.333,0,0,1,9.49,1.8724h1.53Zm1.8367.0634V1.8724H15c.8729,0,2.9046,1.3011,4.6108,2.724ZM8.4184,11.3622a2.6017,2.6017,0,0,1-2.6013,2.6021H5.8163a2.6017,2.6017,0,0,1-2.602-2.6014v-.0007A2.6017,2.6017,0,0,1,5.8155,8.76h.0008a2.6017,2.6017,0,0,1,2.6021,2.6013Zm18.3673,0a2.6019,2.6019,0,1,1,0-.0007Z',
    markerDefaultActive: (activeColor: string): string =>
        `data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="32" height="38" viewBox="0 0 32 38"%3E%3Cpath d="M 15.75 1.484 A 14.3505 14.3505 90 0 1 24.649 27.092 L 16.8 35 C 16.1 35.7 15.4 35.7 14.7 35 L 6.8503 27.0915 A 14.3505 14.3505 90 0 1 15.75 1.484 Z" fill-rule="evenodd"/%3E%3Cpath d="M 15.75 0.784 A 15.0505 15.0505 90 0 1 25.112 27.6183 h 0 L 16.8 35.7 C 16.1 36.4 15.4 36.4 14.7 35.7 L 6.3872 27.6177 A 15.0504 15.0504 90 0 1 15.75 0.784 Z" fill="%23fff" stroke="%23000" stroke-width="2" fill-rule="evenodd"/%3E%3Ccircle cx="16" cy="16" r="10" fill="%23${activeColor}"/%3E%3Cpath /%3E%3C/svg%3E`,
    startIcon:
        'data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 32 32"%3E%3Cpath d="M31,16A15,15,0,1,1,16,1,15,15,0,0,1,31,16Z" fill="%23B5D100"/%3E%3Cpath d="M16,32A16,16,0,1,1,32,16,16.0183,16.0183,0,0,1,16,32ZM16,2A14,14,0,1,0,30,16,14.0158,14.0158,0,0,0,16,2Z"/%3E%3C/svg%3E',
    eventIcon:
        'data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 32 32"%3E%3Cpath d="M31,16A15,15,0,1,1,16,1,15,15,0,0,1,31,16Z" fill="%230e719e"/%3E%3Cpath d="M16,32A16,16,0,1,1,32,16,16.0183,16.0183,0,0,1,16,32ZM16,2A14,14,0,1,0,30,16,14.0158,14.0158,0,0,0,16,2Z"/%3E%3C/svg%3E',
    endIcon:
        'data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 32 32"%3E%3Cpath d="M31,16A15,15,0,1,1,16,1,15,15,0,0,1,31,16Z" fill="%23E03905"/%3E%3Cpath d="M16,32A16,16,0,1,1,32,16,16.0183,16.0183,0,0,1,16,32ZM16,2A14,14,0,1,0,30,16,14.0158,14.0158,0,0,0,16,2Z"/%3E%3C/svg%3E',
    dataStampIcon: (activeColor: string): string =>
        `data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 32 32"%3E%3Cpath d="M31,16A15,15,0,1,1,16,1,15,15,0,0,1,31,16Z" fill="%23fff"/%3E%3Cpath d="M16,32A16,16,0,1,1,32,16,16.0183,16.0183,0,0,1,16,32ZM16,2A14,14,0,1,0,30,16,14.0158,14.0158,0,0,0,16,2Z" fill="%23${activeColor}"/%3E%3C/svg%3E`,
    pathIcon:
        'data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 32 32"%3E%3Cpath d="M31,16A15,15,0,1,1,16,1,15,15,0,0,1,31,16Z" fill="%23000"/%3E%3Cpath d="M16,32A16,16,0,1,1,32,16,16.0183,16.0183,0,0,1,16,32ZM16,2A14,14,0,1,0,30,16,14.0158,14.0158,0,0,0,16,2Z"/%3E%3C/svg%3E',
    stopIcon: (color: string): string =>
        `data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 32 32"%3E%3Cpath d="M31,16A15,15,0,1,1,16,1,15,15,0,0,1,31,16Z" fill="%23${color.substring(
            1,
        )}"/%3E%3Cpath /%3E%3C/svg%3E`,
};

const CONSENT_INFOMODAL = {
    pathContent: (data: ApiDeviceRoutesInfomodalContent): string => `<div id="content">
        <p style="border-bottom: 1px solid #666666; font-size: 13px"><strong>${data.route.name} <span style="color: ${
        data.route.keyColor
    }">${data.route.key}</span> ${data.route.value}</strong></p>
        <p style="font-size: 12px"><b>${data.timeS.name}:</b> ${data.timeS.value}</p>
        <p style="font-size: 12px"><b>${data.height.name}:</b> ${data.height.value}</p>
        <p style="font-size: 12px"><b>${data.speed.name}:</b> ${data.speed.value}</p>
        ${
            data.temp01.value
                ? `<p style="font-size: 12px"><b>${data.temp01.name}:</b> ${data.temp01.value}&deg</p>`
                : ''
        }
        ${
            data.temp02.value
                ? `<p style="font-size: 12px"><b>${data.temp02.name}:</b> ${data.temp02.value}&deg</p>`
                : ''
        }
        ${
            data.temp03.value
                ? `<p style="font-size: 12px"><b>${data.temp03.name}:</b> ${data.temp03.value}&deg</p>`
                : ''
        }
        ${
            data.temp04.value
                ? `<p style="font-size: 12px"><b>${data.temp04.name}:</b> ${data.temp04.value}&deg</p>`
                : ''
        }
    </div>`,
    stopContent: (data: ApiDeviceRoutesInfomodalContent): string => `<div id="content">
        <p style="border-bottom: 1px solid #666666; font-size: 13px"><strong>${data.route.name} <span style="color: ${data.route.keyColor}">${data.route.key}</span> - ${data.stopPoint.name} ${data.stopPoint.value}</strong></p>
        <p style="font-size: 12px"><b>${data.timeS.name}:</b> ${data.timeS.value} - ${data.timeE?.value}</p>
        <p style="font-size: 12px"><b>${data.speed.name}:</b> ${data.speed.value}</p>
        <p style="border-top: 1px solid #666666; font-size: 11px">${data.address.street}, ${data.address.postCode} ${data.address.city}</p>
    </div>`,
    startEndContent: (data: ApiDeviceRoutesInfomodalContent): string => `<div id="content">
        <p style="border-bottom: 1px solid #666666; font-size: 13px"><strong>${data.route.name} <span style="color: ${
        data.route.keyColor
    }">${data.route.key}</span> - ${data.stopPoint.name}</strong></p>
        <p style="font-size: 12px"><b>${data.timeS.name}:</b> ${data.timeS.value}</p>
        <p style="font-size: 12px"><b>${data.height.name}:</b> ${data.height.value}</p>
        ${
            data.temp01.value
                ? `<p style="font-size: 12px"><b>${data.temp01.name}:</b> ${data.temp01.value}&deg</p>`
                : ''
        }
        ${
            data.temp02.value
                ? `<p style="font-size: 12px"><b>${data.temp02.name}:</b> ${data.temp02.value}&deg</p>`
                : ''
        }
        ${
            data.temp03.value
                ? `<p style="font-size: 12px"><b>${data.temp03.name}:</b> ${data.temp03.value}&deg</p>`
                : ''
        }
        ${
            data.temp04.value
                ? `<p style="font-size: 12px"><b>${data.temp04.name}:</b> ${data.temp04.value}&deg</p>`
                : ''
        }
        <p style="border-top: 1px solid #666666; font-size: 11px">${data.address.street}, ${data.address.postCode} ${
        data.address.city
    }</p>
    </div>`,
    deviceContent: (data: ApiDeviceInfomodalContent): string => `<div id="content-device">
        <p style="font-size: 14px; margin-bottom: 10px; border-bottom: 1px solid #666666; font-weight: bold;">${
            data.name.value
        }</p>
        <p style="font-size: 12px"><b>${data.altitude.name}:</b> ${data.altitude.value}</p>
        <p style="font-size: 12px"><b>${data.lastPosition.name}:</b> ${data.lastPosition.value}</p>
        <p style="border-bottom: 1px solid #666666; font-size: 12px"><b>${data.lastContact.name}:</b> ${
        data.lastContact.value
    }</p>
        <p style="font-size: 11px">${data.address.street}, ${data.address.postCode} ${data.address.city}</p>
        ${data.showDetails ? `<p id="infowindow-content" style="font-size: 12px"></p>` : ''}
        ${
            data.showDetails
                ? `<button style="width: 24px; height: 24px; background: #fff; cursor: pointer; margin-top: 4px; float: center;" onclick="document.getElementById('infowindow-content').innerHTML += '<br><b>${data.details.captionLabel}:</b> ${data.details.caption} <br><b>${data.details.codeLabel}:</b> ${data.details.code}'; this.remove();" id="show-device-details-btn"><svg viewBox="0 0 48 48" style="width: 24px; height: 24px;">
                <path d="M0 0h48v48h-48z" fill="none" />
                <path
                    d="M22 34h4v-12h-4v12zm2-30c-11.05 0-20 8.95-20 20s8.95 20 20 20 20-8.95 20-20-8.95-20-20-20zm0 36c-8.82 0-16-7.18-16-16s7.18-16 16-16 16 7.18 16 16-7.18 16-16 16zm-2-22h4v-4h-4v4z"
                />
            </svg></button>`
                : ''
        }
    </div>`,
    poiContent: (data: ApiDevicePoiInfomodalContent): string => `<div id="content-device" style="max-width: 300px;">
        <p style="font-size: 14px; margin-bottom: 5px; border-bottom: 1px solid #666666; font-weight: bold;">${data.name.value}</p>
        <p style="font-size: 12px">${data.description.value}</p>
    </div>`,
};

export default class Map {
    map: google.maps.Map | null = null;
    mapElement: HTMLElement | null = null;
    mapReady = false;
    onMapReady: ((withZoom: boolean) => void) | null = null;
    searchBoxElement: HTMLInputElement | null = null;
    filterSearchBoxElement: HTMLInputElement | null = null;
    searchBox: google.maps.places.SearchBox | null = null;
    filterSearch: google.maps.places.SearchBox | null = null;
    searchActivate: ((active: boolean) => void) | undefined;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    filterSearchGetValue: (value: FilteredSearchLocation) => void = () => {};

    markers: Array<google.maps.Marker> = [];
    markersSearch: Array<google.maps.Marker> = [];
    markersNearbySearch: Array<google.maps.Marker> = [];
    markerCluster: MarkerClusterer | null = null;

    paths: Array<google.maps.Polyline> = [];
    routePath: google.maps.Polyline;
    pathsMarkers: Array<google.maps.Marker> = [];
    pathInfoWindow: google.maps.InfoWindow | null = null;
    pathAnimatedInfoWindow: google.maps.InfoWindow | null = null;

    layerTraffic: google.maps.TrafficLayer | null = null;
    layers: Array<google.maps.Polygon | google.maps.Marker> = [];
    layersInfoWindow: google.maps.InfoWindow | null = null;

    fences: Array<google.maps.Polygon> = [];

    routeAnimationInterval: number | undefined;
    markerAnimation: google.maps.Marker | undefined | null;
    routesAnimation:
        | (google.maps.LatLng | google.maps.LatLngLiteral | null | undefined)[]
        | { lat: number; lng: number }[] = [];

    routeAnimationPaused = false;
    routeAnimationProgress = 0;
    routeAnimationClearFunction: (() => void) | undefined;
    clearNearDevicesSearch: (() => void) | undefined;

    initialize(
        mapElement: HTMLElement,
        searchBoxElement: HTMLInputElement,
        filterBoxElement: HTMLInputElement, //nearby search
        showPlaces: () => void,
        onMapReady: () => void,
        searchActivate: (active: boolean) => void,
        clearNearDevicesSearch: () => void,
    ): void {
        this.mapElement = mapElement;
        this.searchBoxElement = searchBoxElement;
        this.filterSearchBoxElement = filterBoxElement;
        this.filterSearchGetValue = showPlaces;
        this.onMapReady = onMapReady;
        this.searchActivate = searchActivate;
        this.clearNearDevicesSearch = clearNearDevicesSearch;
    }

    loadScript(mapId = '', reloadOnlyMapType = false): boolean {
        const scriptId = 'map-api-script';
        const mapAttached = !!document.getElementById(scriptId);
        const defaultZoom = reloadOnlyMapType ? this.map?.getZoom() ?? 12 : 12;
        const defaultCenter = reloadOnlyMapType ? this.map?.getCenter() : { lat: 47.373878, lng: 8.545094 };
        const withZoom = !reloadOnlyMapType;

        if (mapAttached) {
            if (window.google) {
                this.init(mapId, defaultCenter, defaultZoom, withZoom);
                if (this.layers.length > 0) this.layersShow(withZoom);
            }
        } else {
            window.mapApiInitialized = () => {
                this.init(mapId, defaultCenter, defaultZoom, withZoom);
                if (this.layers.length > 0) this.layersShow(withZoom);
            };

            const apiKey = 'AIzaSyBtFYz8AyIxH1lk9hahlWBy1TFCBNTdEHE';
            const script = document.createElement('script');
            script.id = scriptId;
            /* script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&callback=mapApiInitialized`; */
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&callback=mapApiInitialized`;
            document.body.appendChild(script);
        }

        return mapAttached;
    }

    init(mapId = '', defaultCenter, defaultZoom = 12, withZoom = true): void {
        this.map = new window.google.maps.Map(this.mapElement as HTMLElement, {
            center: defaultCenter,
            zoom: defaultZoom,
            maxZoom: 20,
            disableDefaultUI: true,
            styles: mapStyles,
            streetViewControl: true,
            streetViewControlOptions: {
                position: google.maps.ControlPosition.TOP_LEFT,
            },
            zoomControl: true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.TOP_LEFT,
            },
            scaleControl: true,
            mapId: mapId,
        });

        this.initSearchBox();
        this.initFilterSearchBox();
        this.changeStoppointsVisibilityBasedZoom();
        this.mapReady = true;
        if (this.onMapReady) {
            this.onMapReady(withZoom);
        }
    }

    fitBounds(onlyPaths: boolean): void {
        const bounds = new window.google.maps.LatLngBounds();
        let anyBound = false;
        if (!onlyPaths && this.markersSearch && this.markersSearch.length > 0) {
            anyBound = true;
            for (let i = 0; i < this.markersSearch.length; i++) {
                bounds.extend(this.markersSearch[i].getPosition() as google.maps.LatLng);
            }
        }
        if (!onlyPaths && this.markersNearbySearch && this.markersNearbySearch.length > 0) {
            anyBound = true;
            for (let i = 0; i < this.markersNearbySearch.length; i++) {
                bounds.extend(this.markersNearbySearch[i].getPosition() as google.maps.LatLng);
            }
        }
        if (!onlyPaths && this.markers && this.markers.length > 0) {
            anyBound = true;
            for (let i = 0; i < this.markers.length; i++) {
                bounds.extend(this.markers[i].getPosition() as google.maps.LatLng);
            }
        }
        if (this.paths && this.paths.length > 0) {
            anyBound = true;
            for (let i = 0; i < this.paths.length; i++) {
                this.paths[i].getPath().forEach(function (item) {
                    bounds.extend(new google.maps.LatLng(item.lat(), item.lng()));
                });
            }
        }

        if (anyBound) {
            this.map?.setOptions({ maxZoom: 18 });

            this.map?.addListener('idle', () => {
                this.map?.setOptions({ maxZoom: 20 });
            });
            this.map?.fitBounds(bounds, { top: 110, right: 80, left: 60 }); // auto-zoom
            this.map?.panToBounds(bounds); // auto-center
        }
    }

    initSearchBox(): void {
        this.searchBox = new window.google.maps.places.SearchBox(this.searchBoxElement as HTMLInputElement);
        this.searchBox.addListener('places_changed', this.searchPlacesChanged.bind(this));
        if (this.map) {
            this.map.addListener('bounds_changed', () => {
                if (this.map && this.searchBox) {
                    this.searchBox.setBounds(this.map.getBounds() as google.maps.LatLngBounds);
                }
            });
        }
    }

    initFilterSearchBox(): void {
        this.filterSearch = new window.google.maps.places.SearchBox(this.filterSearchBoxElement as HTMLInputElement);
        this.filterSearch.addListener('places_changed', this.searchNearbyPlaces.bind(this));
        if (this.map) {
            this.map.addListener('bounds_changed', () => {
                if (this.map && this.filterSearch) {
                    this.filterSearch.setBounds(this.map.getBounds() as google.maps.LatLngBounds);
                }
            });
        }
    }

    searchPlacesChanged(): void {
        const bounds = new google.maps.LatLngBounds();

        const places = this.searchBox?.getPlaces();
        if (!places || !places?.length) {
            return;
        }

        // Clear out the old markers.
        this.searchClear();

        // For each place, get the icon, name and location.
        places?.forEach((place) => {
            if (!place.geometry || !place.geometry.location) {
                // console.log('Returned place contains no geometry');
                return;
            }

            const icon = {
                url: place.icon as string,
                size: new window.google.maps.Size(71, 71),
                origin: new window.google.maps.Point(0, 0),
                anchor: new window.google.maps.Point(17, 34),
                scaledSize: new window.google.maps.Size(25, 25),
            };

            const marker = new window.google.maps.Marker({
                map: this.map as google.maps.Map,
                icon,
                title: place.name,
                position: place.geometry.location,
            });

            // Create a marker for each place.
            this.markersSearch.push(marker);
            bounds.extend(place.geometry.location);
        });

        // need to expand bounds because for one location zoom is too close
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
            // create fake points
            const extendPoint1 = new google.maps.LatLng(
                bounds.getNorthEast().lat() + 0.03,
                bounds.getNorthEast().lng() + 0.03,
            );
            const extendPoint2 = new google.maps.LatLng(
                bounds.getNorthEast().lat() - 0.03,
                bounds.getNorthEast().lng() - 0.03,
            );

            bounds.extend(extendPoint1);
            bounds.extend(extendPoint2);
        }

        this.map?.fitBounds(bounds); // auto-zoom
        this.map?.panToBounds(bounds); // auto-center

        if (this.searchActivate) {
            this.searchActivate(this.markersSearch.length > 0);
        }
    }

    searchNearbyPlaces(): void {
        const places = this.filterSearch?.getPlaces();
        const bounds = new google.maps.LatLngBounds();

        if (!places || !places?.length) {
            return;
        }

        // For each place, get the icon, name and location.
        places?.forEach((place) => {
            const icon = {
                url: place.icon as string,
                size: new window.google.maps.Size(71, 71),
                origin: new window.google.maps.Point(0, 0),
                anchor: new window.google.maps.Point(17, 34),
                scaledSize: new window.google.maps.Size(25, 25),
            };

            const marker = new window.google.maps.Marker({
                map: this.map as google.maps.Map,
                icon,
                title: place.name,
                position: place.geometry.location,
            });

            marker.addListener('click', () => {
                this.markersNearbyPlacesClear();
                this.clearNearDevicesSearch();
            });

            //Clear existing markers
            //this.markersClear();
            this.markersNearbyPlacesClear();

            // Create a marker for each place.
            this.markersNearbySearch.push(marker);

            bounds.extend(place.geometry.location);

            this.filterSearchGetValue({
                name: place.formatted_address,
                latLng: place.geometry.location as google.maps.LatLng,
            });
        });

        this.map?.fitBounds(bounds); // auto-zoom
        this.map?.panToBounds(bounds); // auto-center
    }

    searchClear(): void {
        this.routeAnimationClear();

        this.markersSearch.forEach((marker) => {
            marker.setMap(null);
        });
        this.markersSearch = [];
        this.fitBounds(false);
    }

    markersNearbyPlacesClear(): void {
        if (this.routePath) {
            this.routePath.setMap(null);
        }
        this.routeAnimationClear();

        this.markersNearbySearch.forEach((marker) => {
            marker.setMap(null);
        });
        this.markersNearbySearch = [];
        this.fitBounds(false);
    }

    markersClear(): void {
        this.routeAnimationClear();

        if (this.markerCluster) {
            this.markerCluster.clearMarkers();
        }

        for (const marker of this.markers) {
            marker.setMap(null);
        }
        this.markers = [];
    }

    markersEventsClear(): void {
        this.markers.forEach((pathsMarker, index) => {
            if (pathsMarker.get('markerType') === 'event') {
                this.markers[index].setMap(null);
                this.markers.splice(index, 1);
            }
        });
    }

    _markerIcon(item: ApiDeviceListItem, isActive: boolean): google.maps.Icon | google.maps.Symbol | string {
        // how to add icon - https://stackoverflow.com/questions/24413766/how-to-use-svg-markers-in-google-maps-api-v3/24426400#24426400
        // how to format svg icon - https://stackoverflow.com/questions/24413766/how-to-use-svg-markers-in-google-maps-api-v3/28816043#28816043

        if (!isActive) {
            if (item.IconName && item.svgIcon === '') {
                return {
                    url: `https://portalbeta.tracker.com/api/trk/common/image/icon/svg/${
                        item.IconName.includes('png') ? item.IconName.replace(/png/g, 'svg') : item.IconName
                    }`,
                    size: new google.maps.Size(45, 54),
                    origin: new google.maps.Point(0, 0),
                    anchor: new google.maps.Point(22, 54),
                    labelOrigin: new google.maps.Point(17, 40),
                    scaledSize: new google.maps.Size(32, 32),
                };
            }
            if (item.svgIcon !== '') {
                const icon = item.IconColor
                    ? item?.svgIcon.replace(/#[0-9A-F]{3,6}/gi, () => item.IconColor)
                    : item?.svgIcon;

                return {
                    url: 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(icon),
                    size: new google.maps.Size(45, 54),
                    origin: new google.maps.Point(0, 0),
                    anchor: new google.maps.Point(22, 54),
                    labelOrigin: new google.maps.Point(17, 40),
                    scaledSize: new google.maps.Size(32, 32),
                };
            }
            if (item.ImageName) {
                return {
                    url: `https://portalbeta.tracker.com/api/trk/common/image/icon/32/${item.ImageName}`,
                    size: new google.maps.Size(45, 54),
                    origin: new google.maps.Point(0, 0),
                    anchor: new google.maps.Point(22, 54),
                    labelOrigin: new google.maps.Point(17, 40),
                    scaledSize: new google.maps.Size(32, 32),
                };
            }
            if (item.HWT.Image) {
                return {
                    url: `https://portalbeta.tracker.com/api/trk/common/image/icon/32/${item.HWT.Image}`,
                    size: new google.maps.Size(45, 54),
                    origin: new google.maps.Point(0, 0),
                    anchor: new google.maps.Point(22, 54),
                    labelOrigin: new google.maps.Point(17, 40),
                    scaledSize: new google.maps.Size(32, 32),
                };
            }
            return {
                path: MAP_ICONS.markerDefault,
                fillColor: '#000000',
                fillOpacity: 0.9,
                strokeWeight: 0,
                scale: 1,
                size: new google.maps.Size(30, 14),
                origin: new google.maps.Point(0, 0),
                labelOrigin: new google.maps.Point(15, 30),
                anchor: new google.maps.Point(15, 7),
            };
        }

        let activeColor = '#fff';
        if (isActive) {
            /* const statusColors = {
                  online: '27e005', // green
                  offline: 'e3e008', // yellow
                  dead: 'e30613', // red
              }; */
            const itemStatus = item.uiStatus;
            activeColor = itemStatus === 'online' ? '27e005' : itemStatus === 'offline' ? 'e3e008' : 'e30613';
        }

        return {
            url: MAP_ICONS.markerDefaultActive(activeColor),
            size: new google.maps.Size(32, 38),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(16, 38),
            labelOrigin: new google.maps.Point(16, 48),
        };
    }

    markersRender(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        t: any,
        routes: ApiDeviceRoutes,
        devices: Array<ApiDeviceListItem>,
        deviceActive: Ref<number | null>,
        handleScrollToDevice: () => void,
        withZoom = true,
        withClustering = true,
        withClusterSize = 2,
        nearLocationPlace: FilteredSearchLocation,
        clearNearByLocationPlace: () => void,
        isToday = true,
        isAdmin = false,
    ): void {
        if (!this.map || !this.mapReady) {
            return;
        }
        this.markersClear();

        if (nearLocationPlace.name !== null) {
            this.markersNearbySearch.forEach((marker) => {
                marker.setMap(this.map);
            });
        }

        const hasActive = deviceActive.value && deviceActive.value > 0;
        if (typeof devices && devices.length > 0) {
            for (let i = 0; i < devices.length; i++) {
                const device = devices[i];
                const isActive = device.Id === deviceActive.value;

                if (!device.LastPos) {
                    continue;
                }

                if (hasActive && !isActive) {
                    continue;
                }

                const colorBackground = deviceBgColorSelector(device.ImageTextBGC, device?.DeviceStatus);

                const markerCurrentPosition = {
                    lat: device.LastPos.Lat,
                    lng: device.LastPos.Lng,
                    street: device.LastPos?.AdrInfo?.Street,
                    postCode: device.LastPos?.AdrInfo?.PC,
                    city: device.LastPos?.AdrInfo?.City,
                    evtDate: device.LastPos?.EvtDate,
                    alt: device.LastPos?.Alt,
                };

                const markerLastRoutePosition = {
                    lat: device.LastPos.Lat,
                    lng: device.LastPos.Lng,
                    street: device.LastPos?.AdrInfo?.Street,
                    postCode: device.LastPos?.AdrInfo?.PC,
                    city: device.LastPos?.AdrInfo?.City,
                    evtDate: device.LastPos?.EvtDate,
                    alt: device.LastPos?.Alt,
                };

                if (routes.List && routes.List.length > 0 && routes.List[routes.List.length - 1].RouteEnd) {
                    markerLastRoutePosition.lat = routes.List[routes.List.length - 1].RouteEnd.Lat;
                    markerLastRoutePosition.lng = routes.List[routes.List.length - 1].RouteEnd.Lng;
                    markerLastRoutePosition.street = routes.List[routes.List.length - 1].RouteEnd?.AdrInfo?.Street;
                    markerLastRoutePosition.postCode = routes.List[routes.List.length - 1].RouteEnd?.AdrInfo?.PC;
                    markerLastRoutePosition.city = routes.List[routes.List.length - 1].RouteEnd?.AdrInfo?.City;
                    markerLastRoutePosition.evtDate = routes.List[routes.List.length - 1].RouteEnd?.EvtDate;
                    markerLastRoutePosition.alt = routes.List[routes.List.length - 1].RouteEnd?.Alt;
                }

                const markerToSetPosition = isToday ? markerCurrentPosition : markerLastRoutePosition;

                const marker = new window.google.maps.Marker({
                    position: {
                        lat: markerToSetPosition.lat,
                        lng: markerToSetPosition.lng,
                    },
                    title: device.Name,
                    // animation: google.maps.Animation.DROP,
                    icon: this._markerIcon(device, isActive),

                    label: {
                        text: device.Name,
                        color: colorsPalette.includes(colorBackground.toLocaleLowerCase())
                            ? device.ImageTextFGC
                            : '#000',
                        className: `marker-label color-${
                            colorsPalette.includes(colorBackground.toLocaleLowerCase())
                                ? colorBackground.substring(1)
                                : 'FFFFFF'
                        }`,
                    },
                });

                marker.set('deviceId', device.Id);

                marker.addListener('click', () => {
                    if (!isActive) {
                        deviceActive.value = marker.get('deviceId'); // TODO
                        setTimeout(() => handleScrollToDevice(), 1000);
                        if (nearLocationPlace.name !== null) {
                            clearNearByLocationPlace();
                            this.markersNearbyPlacesClear();
                        }
                    } else {
                        const isDeviceInfoWindowOpened = this.pathInfoWindow
                            ?.getContent()
                            ?.toString()
                            .search('content-device');

                        if (!isDeviceInfoWindowOpened || isDeviceInfoWindowOpened === -1) {
                            this.pathInfoWindow = new google.maps.InfoWindow();
                            this.pathInfoWindow?.setContent(
                                CONSENT_INFOMODAL.deviceContent({
                                    name: {
                                        name: '',
                                        value: device.Name,
                                    },
                                    altitude: {
                                        name: t('pages.maps.devices_map.deviceInfo.altitude'),
                                        value: !markerToSetPosition.alt ? '' : markerToSetPosition.alt,
                                    },
                                    lastPosition: {
                                        name: t('pages.maps.devices_map.deviceInfo.lastPosition'),
                                        value: format(new Date(markerToSetPosition.evtDate), 'dd.MM.yyyy, HH:mm'),
                                    },
                                    lastContact: {
                                        name: t('pages.maps.devices_map.deviceInfo.lastContact'),
                                        value: format(new Date(device.LastContact), 'dd.MM.yyyy, HH:mm'),
                                    },
                                    address: {
                                        street: markerToSetPosition.street,
                                        postCode: markerToSetPosition.postCode,
                                        city: markerToSetPosition.city,
                                    },
                                    details: {
                                        captionLabel: t('pages.maps.devices_list.caption'),
                                        codeLabel: t('pages.maps.devices_list.code'),
                                        caption: device.HWT.Caption,
                                        code: device.Code,
                                    },
                                    showDetails: isAdmin,
                                }),
                            );
                        }

                        this.pathInfoWindow?.setPosition({
                            lat: markerToSetPosition.lat,
                            lng: markerToSetPosition.lng,
                        });
                        this.pathInfoWindow?.setOptions({
                            pixelOffset: new google.maps.Size(0, -50),
                        });
                        this.pathInfoWindow?.open(this.map);
                        // deviceInfoDiagram
                    }
                });

                if (!withClustering) {
                    marker.setMap(this.map);
                }

                this.markers.push(marker);
            }

            if (withClustering) {
                this.markerCluster = new MarkerClusterer(this.map, this.markers, {
                    imagePath: '/images/markerclusterer/m',
                    styles: markerClusterStyles,
                    minimumClusterSize: withClusterSize,
                    maxZoom: 17,
                });
            }

            if (withZoom) {
                this.fitBounds(false);
            }
        }
    }

    polylineCalculateClosestPoint(polyline: google.maps.Polyline, latlng: google.maps.LatLng) {
        const needle = {
            minDistance: 9999999999, // silly high
            index: -1,
            latlng: null as unknown,
        };
        polyline.getPath().forEach((routePoint, index) => {
            const dist = google.maps.geometry.spherical.computeDistanceBetween(latlng, routePoint);
            if (dist < needle.minDistance) {
                needle.minDistance = dist;
                needle.index = index;
                needle.latlng = routePoint;
            }
        });
        return needle;
    }

    pathInfoDiagram(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        t: any,
        item: ApiDeviceRoutesListItem,
        path: google.maps.Polyline,
        updateRouteDetails: (offsetKey: string) => void,
    ): void {
        this.pathInfoWindow = new google.maps.InfoWindow();

        path.addListener('click', (h: { latLng: google.maps.LatLng }) => {
            const latlng = h.latLng;

            const needle = this.polylineCalculateClosestPoint(path, latlng);

            const pos = item.Pos[needle.index] as ApiDeviceRoutesListItemPos;
            const routeDistance = item.LenghtTrimmedKm;
            const offsetKey = item.OffsetKey;

            this.pathInfoWindow?.setContent(
                CONSENT_INFOMODAL.pathContent({
                    route: {
                        name: `${t('pages.maps.devices_map.routeInfo.route')}`,
                        key: `${offsetKey}`,
                        keyColor: item.Color,
                        value: `(~${routeDistance}km)`,
                    },
                    stopPoint: {
                        name: '',
                        value: '',
                    },
                    timeS: {
                        name: t('pages.maps.devices_map.routeInfo.time'),
                        value: format(new Date(pos.EvtDate), 'HH:mm'),
                    },
                    timeE: {
                        name: '',
                        value: '',
                    },
                    height: {
                        name: t('pages.maps.devices_map.routeInfo.height'),
                        value: pos.Alt ? `${pos.Alt}m` : '',
                    },
                    speed: {
                        name: t('pages.maps.devices_map.routeInfo.speed'),
                        value: pos.Spd ? `${Math.floor(pos.Spd * 3.6)}km/h` : '', // round down
                    },
                    temp01: {
                        name: 'Temp 1',
                        value: null,
                    },
                    temp02: {
                        name: 'Temp 2',
                        value: null,
                    },
                    temp03: {
                        name: 'Temp 3',
                        value: null,
                    },
                    temp04: {
                        name: 'Temp 4',
                        value: null,
                    },
                    address: {
                        street: '',
                        postCode: '',
                        city: '',
                    },
                }),
            );
            this.pathInfoWindow?.setPosition(needle.latlng as google.maps.LatLng);
            this.pathInfoWindow?.setOptions({
                pixelOffset: new google.maps.Size(0, 0),
            });
            this.pathInfoWindow?.open(this.map);
            updateRouteDetails(offsetKey);
        });
    }

    stopOnPathInfoDiagram(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        t: any,
        routeKey: string,
        routeColor: string,
        routeDistance: number,
        point: google.maps.Marker,
        stop: ApiDeviceRoutesListItemPos,
        updateRouteDetails: (offsetKey: string) => void,
    ): void {
        this.pathInfoWindow = new google.maps.InfoWindow();

        const latLng = point.getPosition();

        point.addListener('click', () => {
            this.pathInfoWindow?.setContent(
                CONSENT_INFOMODAL.pathContent({
                    route: {
                        name: `${t('pages.maps.devices_map.routeInfo.route')}`,
                        key: `${routeKey}`,
                        keyColor: routeColor,
                        value: `(~${routeDistance}km)`,
                    },
                    stopPoint: {
                        name: '',
                        value: '',
                    },
                    timeS: {
                        name: t('pages.maps.devices_map.routeInfo.time'),
                        value: format(new Date(stop.EvtDate), 'HH:mm'),
                    },
                    timeE: {
                        name: '',
                        value: '',
                    },
                    height: {
                        name: t('pages.maps.devices_map.routeInfo.height'),
                        value: stop.Alt ? `${stop.Alt}m` : '',
                    },
                    speed: {
                        name: t('pages.maps.devices_map.routeInfo.speed'),
                        value: stop.Spd ? `${Math.floor(stop.Spd * 3.6)}km/h` : '', // round down
                    },
                    temp01: {
                        name: 'Temp 1',
                        value: stop.Temp01?.toFixed(1),
                    },
                    temp02: {
                        name: 'Temp 2',
                        value: stop.Temp02?.toFixed(1),
                    },
                    temp03: {
                        name: 'Temp 3',
                        value: stop.Temp03?.toFixed(1),
                    },
                    temp04: {
                        name: 'Temp 4',
                        value: stop.Temp04?.toFixed(1),
                    },
                    address: {
                        street: '',
                        postCode: '',
                        city: '',
                    },
                }),
            );
            this.pathInfoWindow?.setPosition(latLng);
            this.pathInfoWindow?.setOptions({
                pixelOffset: new google.maps.Size(0, 0),
            });
            this.pathInfoWindow?.open(this.map);
            updateRouteDetails(routeKey);
        });
    }

    stopInfoDiagram(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        t: any,
        routeKey: string,
        routeColor: string,
        point: google.maps.Marker,
        stop: ApiDeviceRoutesListItemStop & ApiDeviceRoutesListItemPos,
        updateRouteDetails: (offsetKey: string) => void,
    ): void {
        this.pathInfoWindow = new google.maps.InfoWindow();

        const latLng = point.getPosition();

        point.addListener('click', () => {
            this.pathInfoWindow?.setContent(
                CONSENT_INFOMODAL.stopContent({
                    route: {
                        name: `${t('pages.maps.devices_map.routeInfo.route')}`,
                        value: '',
                        key: `${routeKey}`,
                        keyColor: routeColor,
                    },
                    stopPoint: {
                        name: `${t('pages.maps.devices_map.routeInfo.routeStop')}`,
                        value: stop.StopOffsetKey,
                    },
                    timeS: {
                        name: t('pages.maps.devices_map.routeInfo.time'),
                        value: format(new Date(stop.StopStartDate), 'HH:mm'),
                    },
                    timeE: {
                        name: '',
                        value: format(new Date(stop.StopEndDate), 'HH:mm'),
                    },
                    height: {
                        name: t('pages.maps.devices_map.routeInfo.height'),
                        value: stop.Alt ? `${stop.Alt}m` : '',
                    },
                    speed: {
                        name: t('pages.maps.devices_map.routeInfo.speed'),
                        value: stop.Spd ? `${Math.floor(stop.Spd * 3.6)}km/h` : '', // round down
                    },
                    address: {
                        street: stop.AdrInfo.Street,
                        postCode: stop.AdrInfo.PC,
                        city: stop.AdrInfo.City,
                    },
                }),
            );
            this.pathInfoWindow?.setPosition(latLng);
            this.pathInfoWindow?.setOptions({
                pixelOffset: new google.maps.Size(0, -10),
            });
            this.pathInfoWindow?.open(this.map);
            updateRouteDetails(routeKey);
        });
    }

    startEndInfoDiagram(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        t: any,
        routeKey: string,
        routeColor: string,
        point: google.maps.Marker,
        stop: ApiDeviceRoutesListItemPos & { type: string },
        updateRouteDetails: (offsetKey: string) => void,
    ): void {
        this.pathInfoWindow = new google.maps.InfoWindow();

        const latLng = point.getPosition();

        point.addListener('click', () => {
            this.pathInfoWindow?.setContent(
                CONSENT_INFOMODAL.startEndContent({
                    route: {
                        name: `${t('pages.maps.devices_map.routeInfo.route')}`,
                        value: '',
                        key: `${routeKey}`,
                        keyColor: routeColor,
                    },
                    stopPoint: {
                        name:
                            stop.type === 'start'
                                ? `${t('pages.maps.devices_map.routeInfo.routeStart')}`
                                : `${t('pages.maps.devices_map.routeInfo.routeEnd')}`,
                        value: stop.StopOffsetKey,
                    },
                    timeS: {
                        name: t('pages.maps.devices_map.routeInfo.time'),
                        value: format(new Date(stop.EvtDate), 'HH:mm'),
                    },
                    timeE: {
                        name: '',
                        value: '',
                    },
                    height: {
                        name: t('pages.maps.devices_map.routeInfo.height'),
                        value: stop.Alt ? `${stop.Alt}m` : '',
                    },
                    address: {
                        street: stop.AdrInfo?.Street,
                        postCode: stop.AdrInfo?.PC,
                        city: stop.AdrInfo?.City,
                    },
                    temp01: {
                        name: 'Temp 1',
                        value: stop?.Temp01?.toFixed(1),
                    },
                    temp02: {
                        name: 'Temp 2',
                        value: stop?.Temp02?.toFixed(1),
                    },
                    temp03: {
                        name: 'Temp 3',
                        value: stop?.Temp03?.toFixed(1),
                    },
                    temp04: {
                        name: 'Temp 4',
                        value: stop?.Temp04?.toFixed(1),
                    },
                }),
            );
            this.pathInfoWindow?.setPosition(latLng);
            this.pathInfoWindow?.setOptions({
                pixelOffset: new google.maps.Size(0, -10),
            });
            this.pathInfoWindow?.open(this.map);
            updateRouteDetails(routeKey);
        });
    }

    pathsClear(): void {
        this.routeAnimationClear();

        for (const path of this.paths) {
            path.setMap(null);
        }
        this.paths = [];

        for (const pathsMarker of this.pathsMarkers) {
            pathsMarker.setMap(null);
        }
        this.pathsMarkers = [];

        this.pathInfoWindow?.close();
        this.pathInfoWindow = null;
    }

    changeStoppointsVisibilityBasedZoom(): void {
        this.map.addListener('zoom_changed', () => {
            this.pathsMarkers.filter((marker) => {
                if (marker.get('stopId') !== undefined) {
                    if (this.map.getZoom() > ZOOM_LEVEL_WHEN_TO_HIDE_ROUTE_STOP_POINTS) {
                        marker.setVisible(true);
                    } else {
                        marker.setVisible(false);
                    }
                }
                if (marker.get('dataStamp') !== undefined) {
                    if (this.map.getZoom() > ZOOM_LEVEL_WHEN_TO_HIDE_ROUTE_DATA_POINTS) {
                        marker.setVisible(true);
                    } else {
                        marker.setVisible(false);
                    }
                }
            });
        });
    }

    pathsRender(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        t: any,
        routes: ApiDeviceRoutes,
        routeActive: string | null,
        show: boolean,
        showRouteDetails: string | boolean,
        showMarkerWithFittingBounds: boolean,
        updateRouteDetails: (offsetKey: string | null) => void,
        skipFitBounds = false,
    ): void {
        this.pathsClear();
        this.markersEventsClear();

        if (!show || typeof routes.List === 'undefined' || !routes.List || routes.List.length === 0) {
            return;
        }

        for (const item of routes.List) {
            if (routeActive !== null && routeActive !== item.OffsetKey) {
                continue;
            }

            this.pathInfoWindow = new google.maps.InfoWindow();

            const startIcon = {
                url: MAP_ICONS.startIcon,
                size: new google.maps.Size(16, 16),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(8, 8),
            };

            const endIcon = {
                url: MAP_ICONS.endIcon,
                size: new google.maps.Size(16, 16),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(8, 8),
            };

            const pathIcon = {
                url: MAP_ICONS.pathIcon,
                size: new google.maps.Size(18, 18),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(9, 9),
            };

            const coordinates = [];
            for (const pos of item.Pos) {
                coordinates.push({
                    lat: pos.Lat,
                    lng: pos.Lng,
                });
            }

            const dataStampIcon = {
                url: MAP_ICONS.dataStampIcon(item.Color.split('#')[1] ?? '000'),
                size: new google.maps.Size(10, 10),
                origin: new google.maps.Point(0, 0),
                anchor: new google.maps.Point(5, 5),
            };

            if (routeActive && showRouteDetails) {
                coordinates.forEach((coordinate, index: number) => {
                    const marker = new window.google.maps.Marker({
                        position: { lat: coordinate.lat, lng: coordinate.lng },
                        map: this.map,
                        icon: dataStampIcon,
                    });
                    marker.set('dataStamp', true);

                    if (index !== 0 || index !== coordinates.length) {
                        this.stopOnPathInfoDiagram(
                            t,
                            item.OffsetKey,
                            item.Color,
                            item.LenghtTrimmedKm,
                            marker,
                            item.Pos[index] as ApiDeviceRoutesListItemPos,
                            updateRouteDetails,
                        );
                        this.pathsMarkers.push(marker);
                    }
                });
                /* this.changeDataStampVisibilityBasedZoom(13); */
            }

            const markerStart = new window.google.maps.Marker({
                position: { lat: item.RouteStart.Lat, lng: item.RouteStart.Lng },
                map: this.map,
                icon: startIcon,
            });

            this.pathsMarkers.push(markerStart);

            this.startEndInfoDiagram(
                t,
                item.OffsetKey,
                item.Color,
                markerStart,
                {
                    ...item.RouteStart,
                    type: 'start',
                },
                updateRouteDetails,
            );

            if (item.StopCount > 0 && item.StopPos && item.StopPos.length > 0) {
                for (let i = 0; i < item.StopCount; i++) {
                    const stop = item.StopPos[i] as ApiDeviceRoutesListItemStop & ApiDeviceRoutesListItemPos;
                    if (stop.Lat && stop.Lng) {
                        const marker = new window.google.maps.Marker({
                            position: { lat: stop.Lat, lng: stop.Lng },
                            map: this.map,
                            label: {
                                text: stop.StopOffsetKey,
                                color: '#fff',
                                fontSize: '11px',
                                className: `path-marker-label`,
                            },
                            icon: pathIcon,
                        });
                        marker.set('stopId', stop.Id);
                        this.pathsMarkers.push(marker);
                        this.stopInfoDiagram(t, item.OffsetKey, item.Color, marker, stop, updateRouteDetails);
                    }
                }
            }

            const markerEnd = new window.google.maps.Marker({
                position: { lat: item.RouteEnd.Lat, lng: item.RouteEnd.Lng },
                map: this.map,
                icon: endIcon,
            });
            this.pathsMarkers.push(markerEnd);

            this.startEndInfoDiagram(
                t,
                item.OffsetKey,
                item.Color,
                markerEnd,
                {
                    ...item.RouteEnd,
                    type: 'end',
                },
                updateRouteDetails,
            );

            const path = new window.google.maps.Polyline({
                path: coordinates,
                geodesic: true,
                strokeColor: item.Color,
                strokeOpacity: 0.6,
                strokeWeight: routeActive && showRouteDetails ? 6 : 3,
                map: this.map,
            });

            if ((routeActive && showRouteDetails) || !routeActive) {
                this.pathInfoDiagram(t, item, path, updateRouteDetails);
            }

            // show route detail
            this.paths.push(path);
        }

        if (!skipFitBounds) {
            this.fitBounds(!showMarkerWithFittingBounds || routeActive !== null);
        }

        if (routeActive && showRouteDetails) {
            this.pathsMarkers.filter((marker) => {
                if (marker.get('dataStamp') !== undefined) {
                    if (this.map.getZoom() > ZOOM_LEVEL_WHEN_TO_HIDE_ROUTE_DATA_POINTS) {
                        marker.setVisible(true);
                    } else {
                        marker.setVisible(false);
                    }
                }
            });
        }
    }

    pathStopFocus(id: number): void {
        this.map?.setOptions({ maxZoom: 18 });
        if (id === -1) {
            try {
                // start marker
                const pathsMarker = this.pathsMarkers[0];
                const bounds = new window.google.maps.LatLngBounds();
                bounds.extend(pathsMarker.getPosition() as google.maps.LatLng);
                this.map?.fitBounds(bounds); // auto-zoom
                this.map?.panToBounds(bounds); // auto-center
            } catch {
                console.log(this.pathsMarkers[0]);
            }
        } else if (id === -2) {
            try {
                // end marker
                const pathsMarker = this.pathsMarkers[this.pathsMarkers.length - 1];
                const bounds = new window.google.maps.LatLngBounds();
                bounds.extend(pathsMarker.getPosition() as google.maps.LatLng);
                this.map?.fitBounds(bounds); // auto-zoom
                this.map?.panToBounds(bounds); // auto-center
            } catch {
                console.log(this.pathsMarkers[this.pathsMarkers.length - 1]);
            }
        } else {
            for (const pathsMarker of this.pathsMarkers) {
                try {
                    if (pathsMarker.get('stopId') === id) {
                        const bounds = new window.google.maps.LatLngBounds();
                        bounds.extend(pathsMarker.getPosition() as google.maps.LatLng);
                        this.map?.fitBounds(bounds); // auto-zoom
                        this.map?.panToBounds(bounds); // auto-center
                    }
                } catch {
                    console.log(pathsMarker);
                }
            }
        }
    }

    showEventWithMarker({ Lat, Lng, Type, Date }: EventMarker): void {
        this.markersEventsClear();

        const bounds = new google.maps.LatLngBounds();
        const eventIcon = {
            url: MAP_ICONS.eventIcon,
            size: new google.maps.Size(16, 16),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(8, 8),
        };

        const markerStart = new window.google.maps.Marker({
            position: { lat: Lat, lng: Lng },
            map: this.map,
            icon: eventIcon,
            zIndex: 100,
        });
        markerStart.set('markerType', 'event');

        this.pathInfoWindow = new google.maps.InfoWindow();

        markerStart.addListener('click', () => {
            this.pathInfoWindow?.setContent(`<div id="content">
                <p style="font-size: 12px">${Date}</p>
                <p style="font-size: 12px">${Type}</p>
            </div>`);
            this.pathInfoWindow?.setPosition({ lat: Lat, lng: Lng });
            this.pathInfoWindow?.setOptions({
                pixelOffset: new google.maps.Size(0, -10),
            });
            this.pathInfoWindow?.open(this.map);
        });

        this.markers = [...this.markers, markerStart];

        /* this.pathsMarkers = [...pathsMarkersTmp]; */

        /* this.markers = []; */

        bounds.extend({
            lat: Lat,
            lng: Lng,
        });

        // need to expand bounds because for one location zoom is too close
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
            // create fake points
            const extendPoint1 = new google.maps.LatLng(
                bounds.getNorthEast().lat() + 0.001,
                bounds.getNorthEast().lng() + 0.001,
            );
            const extendPoint2 = new google.maps.LatLng(
                bounds.getNorthEast().lat() - 0.001,
                bounds.getNorthEast().lng() - 0.001,
            );

            bounds.extend(extendPoint1);
            bounds.extend(extendPoint2);
        }

        this.map?.fitBounds(bounds); // auto-zoom
        this.map?.panToBounds(bounds); // auto-center
    }

    showPositionByCoordinates({ Lat, Lng }: ApiCoordinates): void {
        const bounds = new google.maps.LatLngBounds();

        bounds.extend({
            lat: Lat,
            lng: Lng,
        });

        // need to expand bounds because for one location zoom is too close
        if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
            // create fake points
            const extendPoint1 = new google.maps.LatLng(
                bounds.getNorthEast().lat() + 0.001,
                bounds.getNorthEast().lng() + 0.001,
            );
            const extendPoint2 = new google.maps.LatLng(
                bounds.getNorthEast().lat() - 0.001,
                bounds.getNorthEast().lng() - 0.001,
            );

            bounds.extend(extendPoint1);
            bounds.extend(extendPoint2);
        }
        this.map?.setOptions({ maxZoom: 18 });
        this.map?.fitBounds(bounds); // auto-zoom
        this.map?.panToBounds(bounds); // auto-center
    }

    layersSetTraffic(active: boolean): void {
        if (!this.layerTraffic) {
            this.layerTraffic = new window.google.maps.TrafficLayer();
        }

        if (active) {
            this.layerTraffic.setMap(this.map);
        } else {
            this.layerTraffic.setMap(null);
        }
    }

    layersToogleTraffic(): void {
        if (!this.layerTraffic) {
            this.layerTraffic = new window.google.maps.TrafficLayer();
        }

        if (this.layerTraffic.get('map')) {
            this.layerTraffic.setMap(null);
        } else {
            this.layerTraffic.setMap(this.map);
        }
    }

    layersClear(): void {
        this.routeAnimationClear();

        for (const layer of this.layers) {
            layer.setMap(null);
        }
        this.layers = [];

        this.layersInfoWindow?.close();
        this.layersInfoWindow = null;
    }

    layersRender(
        layers: ApiDeviceLayers,
        layersForFilter: Array<ILayerForFilter>,
        layersActive: ILayersActive | undefined,
    ): void {
        this.layersClear();

        this.layersInfoWindow = new google.maps.InfoWindow();

        for (const layerIndex in layersActive) {
            if (layersActive[layerIndex] !== true) {
                continue;
            }

            const layerFromFilter = layersForFilter.find(({ id }) => `layer-${id}` === layerIndex);
            if (layerFromFilter?.type === 'Category') {
                const layerPoints = layers?.List?.filter((i) => i.Category?.Id === layerFromFilter.id);
                if (typeof layerPoints !== 'undefined') {
                    for (const layer of layerPoints) {
                        if (layer?.GeographyType === 'Polygon') {
                            const coordinates = [];
                            for (const pos of layer?.ShapeCoordinates ?? []) {
                                coordinates.push({
                                    lat: pos.Lat,
                                    lng: pos.Lng,
                                });
                            }

                            const path = new window.google.maps.Polygon({
                                paths: coordinates,
                                geodesic: true,
                                strokeColor: layer?.Color,
                                strokeOpacity: 1.0,
                                strokeWeight: 2,
                                fillColor: layer?.Color,
                                map: this.map,
                            });

                            path.set('layerId', layerIndex);
                            path.set(
                                'textcontent',
                                CONSENT_INFOMODAL.poiContent({
                                    name: {
                                        name: '',
                                        value: layer.Name || '',
                                    },
                                    description: {
                                        name: '',
                                        value: layer.Description?.replace(/\r\n|\r|\n/g, '<br />') || '',
                                    },
                                }),
                            );

                            path.addListener('click', () => {
                                const bounds = new google.maps.LatLngBounds();
                                this.layersInfoWindow?.setContent(path.get('textcontent'));
                                path.getPath().forEach(function (p) {
                                    bounds.extend(p);
                                });

                                this.layersInfoWindow?.setPosition(bounds.getCenter());
                                this.layersInfoWindow?.open(this.map);
                            });

                            this.layers.push(path);
                        } else {
                            const coordinates = [];
                            for (const pos of layer?.ShapeCoordinates ?? []) {
                                coordinates.push({
                                    lat: pos.Lat,
                                    lng: pos.Lng,
                                });
                            }
                            const marker = new window.google.maps.Marker({
                                map: this.map as google.maps.Map,
                                title: layer.Name,
                                position: coordinates[0],
                                icon: layer.ImageName
                                    ? `https://portalbeta.tracker.com/api/trk/common/image/icon/32/${layer.ImageName}`
                                    : null,
                            });

                            marker.set('layerId', layerIndex);
                            /* marker.set('textcontent', layer.Description?.replace(/\r\n|\r|\n/g, '<br />') || layer.Name); */
                            marker.set(
                                'textcontent',
                                CONSENT_INFOMODAL.poiContent({
                                    name: {
                                        name: '',
                                        value: layer.Name || '',
                                    },
                                    description: {
                                        name: '',
                                        value: layer.Description?.replace(/\r\n|\r|\n/g, '<br />') || '',
                                    },
                                }),
                            );
                            marker.addListener('click', () => {
                                this.layersInfoWindow?.setContent(marker.get('textcontent'));
                                this.layersInfoWindow?.setPosition(marker.getPosition() as google.maps.LatLng);
                                this.layersInfoWindow?.setOptions({
                                    pixelOffset: new google.maps.Size(0, -30),
                                });
                                this.layersInfoWindow?.open(this.map);
                            });
                            this.layers.push(marker);
                        }
                    }
                }
            }
        }
    }

    layerFind(layerId: string): void {
        this.routeAnimationClear();

        const bounds = new google.maps.LatLngBounds();
        for (const layer of this.layers) {
            if (layerId === layer.get('layerId')) {
                if (layer instanceof window.google.maps.Polygon) {
                    layer.getPath().forEach(function (path) {
                        bounds.extend(path);
                    });
                } else if (layer instanceof window.google.maps.Marker) {
                    if (layer.getPosition()) {
                        bounds.extend(layer.getPosition() as google.maps.LatLng);
                    }
                }
            }
        }
        this.map?.fitBounds(bounds); // auto-zoom
        this.map?.panToBounds(bounds); // auto-center
    }

    layersShow(withZoom = true): void {
        this.routeAnimationClear();

        const bounds = new google.maps.LatLngBounds();
        for (const layer of this.layers) {
            if (layer instanceof window.google.maps.Polygon) {
                layer.getPath().forEach(function (path) {
                    bounds.extend(path);
                });
            } else if (layer instanceof window.google.maps.Marker) {
                if (layer.getPosition()) {
                    bounds.extend(layer.getPosition() as google.maps.LatLng);
                }
            }
        }
        if (withZoom) {
            this.map?.fitBounds(bounds); // auto-zoom
            this.map?.panToBounds(bounds); // auto-center
        }
    }

    fencesClear(): void {
        this.routeAnimationClear();

        for (const fence of this.fences) {
            fence.setMap(null);
        }
        this.fences = [];
    }

    fencesRender(fences: Array<ApiDeviceActionsItem>, fencesActive: IFencesActive): void {
        this.fencesClear();

        for (const fenceIndex in fencesActive) {
            if (fencesActive[fenceIndex] !== true) {
                continue;
            }

            const fence = fences.find(({ Id }) => `fence-${Id}` === fenceIndex);
            const coordinates = [];
            for (const pos of fence?.ShapeCoordinates ?? []) {
                coordinates.push({
                    lat: pos.Lat,
                    lng: pos.Lng,
                });
            }

            const path = new window.google.maps.Polygon({
                paths: coordinates,
                geodesic: true,
                strokeColor: '#f00',
                strokeOpacity: 1.0,
                strokeWeight: 2,
                fillColor: '#f00',
                map: this.map,
            });
            path.set('fenceId', fenceIndex);

            path.set(
                'textcontent',
                CONSENT_INFOMODAL.poiContent({
                    name: {
                        name: '',
                        value: fence?.Name || '',
                    },
                    description: {
                        name: '',
                        value: fence?.Description?.replace(/\r\n|\r|\n/g, '<br />') || '',
                    },
                }),
            );

            path.addListener('click', () => {
                const bounds = new google.maps.LatLngBounds();
                this.layersInfoWindow?.setContent(path.get('textcontent'));
                path.getPath().forEach(function (p) {
                    bounds.extend(p);
                });

                this.layersInfoWindow?.setPosition(bounds.getCenter());
                this.layersInfoWindow?.open(this.map);
            });

            this.fences.push(path);
        }
    }

    fenceFind(fenceId: string): void {
        this.routeAnimationClear();

        const bounds = new google.maps.LatLngBounds();
        for (const fence of this.fences) {
            if (fenceId === fence.get('fenceId')) {
                if (fence instanceof window.google.maps.Polygon) {
                    fence.getPath().forEach(function (path) {
                        bounds.extend(path);
                    });
                }
            }
        }
        this.map?.fitBounds(bounds); // auto-zoom
        this.map?.panToBounds(bounds); // auto-center
    }

    fenceShow(): void {
        this.routeAnimationClear();

        const bounds = new google.maps.LatLngBounds();
        for (const fence of this.fences) {
            if (fence instanceof window.google.maps.Polygon) {
                fence.getPath().forEach(function (path) {
                    bounds.extend(path);
                });
            }
        }
        this.map?.fitBounds(bounds); // auto-zoom
        this.map?.panToBounds(bounds); // auto-center
    }

    routeAnimationStart(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        t: any,
        routeAnimationClearFunction: () => void,
        routes: ApiDeviceRoutes,
        routeActive: string | null,
    ): void {
        this.routeAnimationClearFunction = routeAnimationClearFunction;
        if (this.paths.length !== 1) {
            return;
        }

        let routeActiveTmp: ApiDeviceRoutesListItem[] = [];

        if (routes.List) {
            routeActiveTmp = routes.List.filter((route: ApiDeviceRoutesListItem) => route.OffsetKey === routeActive);
        }

        if (this.routeAnimationPaused === false) {
            this.routeAnimationClear();

            const bounds = new window.google.maps.LatLngBounds();

            this.paths[0].getPath().forEach((item) => {
                this.routesAnimation.push({
                    lat: item.lat(),
                    lng: item.lng(),
                });
                bounds.extend(new google.maps.LatLng(item.lat(), item.lng()));
            });

            this.map?.fitBounds(bounds); // auto-zoom
            this.map?.panToBounds(bounds); // auto-center

            this.routeAnimationProgress = 0;
            this.pathAnimatedInfoWindow = new google.maps.InfoWindow();
            this.pathAnimatedInfoWindow?.setOptions({
                pixelOffset: new google.maps.Size(0, 0),
            });
            this.pathAnimatedInfoWindow?.open(this.map);
            this.markerAnimation = new window.google.maps.Marker({
                map: this.map,
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    strokeColor: routeActiveTmp[0].Color,
                },
            });
        }

        this.routeAnimationInterval = setInterval(() => {
            if (this.routeAnimationProgress >= routeActiveTmp[0].Pos.length) {
                if (this.routeAnimationClearFunction) {
                    this.routeAnimationClearFunction();
                }
                this.routeAnimationClear();
            }
            this.pathAnimatedInfoWindow?.setPosition({
                lat: routeActiveTmp[0].Pos[this.routeAnimationProgress].Lat,
                lng: routeActiveTmp[0].Pos[this.routeAnimationProgress].Lng,
            });
            this.pathAnimatedInfoWindow?.setContent(
                CONSENT_INFOMODAL.pathContent({
                    route: {
                        name: `${t('pages.maps.devices_map.routeInfo.route')}`,
                        key: `${routeActiveTmp[0].OffsetKey}`,
                        keyColor: routeActiveTmp[0].Color,
                        value: `(~${routeActiveTmp[0].LenghtTrimmedKm}km)`,
                    },
                    stopPoint: {
                        name: '',
                        value: '',
                    },
                    timeS: {
                        name: t('pages.maps.devices_map.routeInfo.time'),
                        value: format(new Date(routeActiveTmp[0].Pos[this.routeAnimationProgress].EvtDate), 'HH:mm'),
                    },
                    timeE: {
                        name: '',
                        value: '',
                    },
                    height: {
                        name: t('pages.maps.devices_map.routeInfo.height'),
                        value: routeActiveTmp[0].Pos[this.routeAnimationProgress].Alt
                            ? `${routeActiveTmp[0].Pos[this.routeAnimationProgress].Alt}m`
                            : '',
                    },
                    speed: {
                        name: t('pages.maps.devices_map.routeInfo.speed'),
                        value: routeActiveTmp[0].Pos[this.routeAnimationProgress].Spd
                            ? `${Math.floor(routeActiveTmp[0].Pos[this.routeAnimationProgress].Spd * 3.6)}km/h`
                            : '', // round down
                    },
                    temp01: {
                        name: 'Temp 1',
                        value: routeActiveTmp[0].Pos[this.routeAnimationProgress].Temp01?.toFixed(1),
                    },
                    temp02: {
                        name: 'Temp 2',
                        value: routeActiveTmp[0].Pos[this.routeAnimationProgress].Temp02?.toFixed(1),
                    },
                    temp03: {
                        name: 'Temp 3',
                        value: routeActiveTmp[0].Pos[this.routeAnimationProgress].Temp03?.toFixed(1),
                    },
                    temp04: {
                        name: 'Temp 4',
                        value: routeActiveTmp[0].Pos[this.routeAnimationProgress].Temp04?.toFixed(1),
                    },
                    address: {
                        street: '',
                        postCode: '',
                        city: '',
                    },
                }),
            );

            if (this.markerAnimation) {
                this.markerAnimation.setPosition({
                    lat: routeActiveTmp[0].Pos[this.routeAnimationProgress].Lat,
                    lng: routeActiveTmp[0].Pos[this.routeAnimationProgress].Lng,
                });
            }

            this.routeAnimationProgress = this.routeAnimationProgress + 1;
        }, 350);
    }

    routeAnimationPause(): void {
        clearInterval(this.routeAnimationInterval);
        this.routeAnimationPaused = true;
    }

    routeAnimationClear(): void {
        clearInterval(this.routeAnimationInterval);
        if (this.paths.length === 1) {
            this.paths[0].set('icons', null);
        }
        this.routeAnimationProgress = 0;
        this.routeAnimationPaused = false;
        this.pathAnimatedInfoWindow?.close();
        this.pathAnimatedInfoWindow = null;
        this.markerAnimation?.setIcon({
            path: google.maps.SymbolPath.CIRCLE,
            scale: 0,
        });
    }

    routeAnimationStop(): void {
        this.routeAnimationClear();
        this.fitBounds(false);
        this.pathAnimatedInfoWindow?.close();
        this.pathAnimatedInfoWindow = null;
    }

    zoomIn(): void {
        this.map?.setZoom((this.map?.getZoom() || 1) + 1);
    }

    zoomOut(): void {
        this.map?.setZoom((this.map?.getZoom() || 1) - 1);
    }

    changeMapType(type: string, terrain: boolean, labels: boolean): void {
        if (type === 'roadmap') {
            this.map?.setMapTypeId(terrain === true ? google.maps.MapTypeId.TERRAIN : google.maps.MapTypeId.ROADMAP);
            this.map?.setOptions({ styles: mapStyles });
        } else if (type === 'satellite') {
            this.map?.setMapTypeId(labels === true ? google.maps.MapTypeId.HYBRID : google.maps.MapTypeId.SATELLITE);
            this.map?.setOptions({ styles: [] });
        } else if (type === 'map_id') {
            this.map?.setMapTypeId(terrain === true ? google.maps.MapTypeId.TERRAIN : google.maps.MapTypeId.ROADMAP);
            this.map?.setOptions({ styles: mapStyles });
        }
    }

    isMapReady(): boolean {
        return this.mapReady;
    }

    showPathAsRoute(coordinates: Array<google.maps.LatLng>): void {
        const bounds = new window.google.maps.LatLngBounds();
        if (this.routePath) {
            this.routePath.setMap(null);
        }
        this.routePath = new window.google.maps.Polyline({
            path: coordinates,
            strokeColor: '#FF00F0',
            strokeOpacity: 1.0,
            strokeWeight: 2,
        });
        this.routePath.setMap(this.map);

        coordinates.forEach(function (item) {
            bounds.extend(item);
        });
        this.map?.fitBounds(bounds, { top: 110, right: 80, left: 60 });
        this.map?.panToBounds(bounds);
    }
}

export interface LocationLatLng {
    lat: number;
    lng: number;
}

export function computeDistance(locationA: LocationLatLng, locationB: google.maps.LatLng | null): number | null {
    if (
        locationA.lat === null ||
        locationA.lng === null ||
        locationA.lat === undefined ||
        locationA.lng === undefined ||
        locationB === null ||
        locationB.lat === null ||
        locationB.lng === null
    ) {
        return null;
    }

    const refLocationA = new google.maps.LatLng(locationA.lat, locationA.lng);
    const refLocationB = locationB;

    const distance = google.maps.geometry.spherical.computeDistanceBetween(refLocationA, refLocationB);

    return distance;
}

export const getCoordinatesAsString = (latLng: google.maps.LatLng) => {
    return {
        lat: latLng.lat(),
        lng: latLng.lng(),
    };
};
